/**
 * Desktop table for the Accounting Recurring transactions page.
 *
 * Two exports:
 *   <RecurringTableHead />  → sticky chrome, pinned at top on scroll
 *   <RecurringTable />      → scrollable body + footer
 *
 * Both read column definitions from recurringColumns.ts (single source
 * of truth). The `visibleColumns` prop drives which columns render,
 * and the grid-template-columns value is computed from that set so
 * hidden columns are REMOVED from the grid (not just zero-width), and
 * remaining columns re-flow to fill the space.
 */

import type { CSSProperties, ReactNode } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import type {
  RecurringTemplate,
  RecurringSortField,
} from "../recurringTypes";
import {
  COLUMNS,
  DEFAULT_VISIBLE_COLUMNS,
  computeGridTemplate,
  type ColumnKey,
  type ColumnDef,
} from "../recurringColumns";
import { RecurringRow } from "./RecurringRow";

/** Map a ColumnKey to the RecurringSortField it sorts by (if any). */
function columnSortField(key: ColumnKey): RecurringSortField | null {
  if (key === "name") return "name";
  if (key === "next") return "nextDate";
  return null;
}

// ---------- Head ----------

export interface RecurringTableHeadProps {
  sortBy: RecurringSortField;
  sortDirection: "asc" | "desc";
  onSortChange: (field: RecurringSortField) => void;
  /** Set of visible column keys. Defaults to all defaults if omitted. */
  visibleColumns?: ReadonlySet<ColumnKey>;
}

export function RecurringTableHead({
  sortBy,
  sortDirection,
  onSortChange,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
}: RecurringTableHeadProps) {
  const cols = COLUMNS.filter((c) => visibleColumns.has(c.key));
  const gridTemplate = computeGridTemplate(visibleColumns);

  return (
    <div
      style={{ ...headRowStyle, gridTemplateColumns: gridTemplate }}
      role="row"
    >
      {cols.map((col) => {
        const sortField = col.sortable ? columnSortField(col.key) : null;
        if (sortField) {
          return (
            <SortableHead
              key={col.key}
              field={sortField}
              sortBy={sortBy}
              direction={sortDirection}
              onSort={onSortChange}
              align={col.rightAlign ? "right" : "left"}
            >
              {col.label}
            </SortableHead>
          );
        }
        return (
          <HeadCell key={col.key} align={col.rightAlign ? "right" : "left"}>
            {col.key === "action" ? "" : col.label}
          </HeadCell>
        );
      })}
    </div>
  );
}

function HeadCell({
  children,
  align,
}: {
  children?: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <div
      role="columnheader"
      style={{
        ...headCellStyle,
        ...(align === "right" ? { textAlign: "right" } : {}),
      }}
    >
      {children}
    </div>
  );
}

function SortableHead({
  field,
  sortBy,
  direction,
  onSort,
  children,
  align,
}: {
  field: RecurringSortField;
  sortBy: RecurringSortField;
  direction: "asc" | "desc";
  onSort: (f: RecurringSortField) => void;
  children: ReactNode;
  align?: "left" | "right";
}) {
  const active = sortBy === field;
  const Arrow = direction === "asc" ? ChevronUp : ChevronDown;
  return (
    <div
      role="columnheader"
      style={{
        ...headCellStyle,
        ...(align === "right" ? { textAlign: "right" } : {}),
      }}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        style={sortButtonStyle}
        aria-sort={
          active ? (direction === "asc" ? "ascending" : "descending") : "none"
        }
      >
        {children}
        {active && <Arrow size={11} strokeWidth={2.5} />}
      </button>
    </div>
  );
}

// ---------- Table (body + footer) ----------

export interface RecurringTableProps {
  templates: RecurringTemplate[];
  /** Total number of templates in the dataset (for the footer count). */
  totalCount: number;
  /**
   * Row three-dot menu opener. Receives the template and the DOM
   * node that was clicked so the parent can anchor a popover.
   */
  onOpenMenu: (template: RecurringTemplate, anchor: HTMLElement) => void;
  /** Set of visible column keys. Defaults to all defaults if omitted. */
  visibleColumns?: ReadonlySet<ColumnKey>;
  /**
   * Legacy sort props. Pass them to render the head inline here
   * instead of in the parent sticky chrome. New callers mount
   * <RecurringTableHead/> above and omit these.
   */
  sortBy?: RecurringSortField;
  sortDirection?: "asc" | "desc";
  onSortChange?: (field: RecurringSortField) => void;
}

export function RecurringTable({
  templates,
  totalCount,
  onOpenMenu,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
  sortBy,
  sortDirection,
  onSortChange,
}: RecurringTableProps) {
  const renderHeadInline =
    sortBy !== undefined &&
    sortDirection !== undefined &&
    onSortChange !== undefined;

  return (
    <div style={tableRootStyle} role="table">
      {renderHeadInline && (
        <RecurringTableHead
          sortBy={sortBy!}
          sortDirection={sortDirection!}
          onSortChange={onSortChange!}
          visibleColumns={visibleColumns}
        />
      )}
      <div role="rowgroup">
        {templates.map((template) => (
          <RecurringRow
            key={template.id}
            template={template}
            visibleColumns={visibleColumns}
            onOpenMenu={(anchor) => onOpenMenu(template, anchor)}
          />
        ))}
      </div>
      <div style={footerStyle}>
        <span>
          {templates.length} of {totalCount}{" "}
          {totalCount === 1 ? "template" : "templates"}
        </span>
        <span>Page 1 of 1</span>
      </div>
    </div>
  );
}

/** Re-export so other files can reach it without importing from the type file directly. */
export type { ColumnDef };

// ---------- Styles ----------

const headRowStyle: CSSProperties = {
  display: "grid",
  columnGap: 14,
  padding: "11px 28px",
  background: "#ffffff",
  borderBottom: "1.5px solid #D8DEE6",
  alignItems: "center",
};

const headCellStyle: CSSProperties = {
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: "#3A4A52",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const sortButtonStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
  color: "#3A4A52",
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontFamily: "inherit",
};

const tableRootStyle: CSSProperties = {
  width: "100%",
  background: "#ffffff",
};

const footerStyle: CSSProperties = {
  padding: "12px 28px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderTop: "1px solid #E7EAF0",
  fontSize: 12,
  color: "#3A4A52",
  background: "#ffffff",
};

export default RecurringTable;